import { useEffect, useState } from "react";
import { api, streamPost } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { SignalCard } from "@/components/SignalCard";
import { StatCard, CardSkeleton } from "@/components/Bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Disclaimer } from "@/components/Disclaimer";
import { toast } from "sonner";

export default function AppSignals() {
  const [signals, setSignals] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [f, setF] = useState({ market: "", direction: "", status: "", min_confidence: 0 });
  const [explain, setExplain] = useState(null);
  const [explainText, setExplainText] = useState("");
  const [share, setShare] = useState(null);

  const load = () => {
    const q = new URLSearchParams();
    if (f.market) q.set("market", f.market);
    if (f.direction) q.set("direction", f.direction);
    if (f.status) q.set("status", f.status);
    if (f.min_confidence) q.set("min_confidence", f.min_confidence);
    q.set("limit", "60");
    api.get(`/signals?${q}`).then((r) => setSignals(r.data.signals)).catch(() => setSignals([]));
  };

  useEffect(() => { load(); }, [f]);
  useEffect(() => { api.get("/signals/analytics").then((r) => setAnalytics(r.data)).catch(() => {}); }, []);

  const doExplain = async (s) => {
    setExplain(s); setExplainText("");
    try {
      await streamPost("/ai/analyst", { symbol: s.asset, timeframe: s.timeframe }, (d) => setExplainText((p) => p + d));
    } catch { setExplainText("AI analysis temporarily unavailable."); }
  };

  const doShare = (s) => setShare(s);
  const copyShare = () => {
    const s = share;
    const text = `KREONIS · ${s.asset} (${s.market}) · ${s.direction} · ${s.timeframe}\nConfidence ${s.confidence}/100 · ${s.quality} · R/R ${s.risk_reward}\nProbabilistic analysis, not financial advice.\nPowered by KREONIS`;
    navigator.clipboard.writeText(text);
    toast.success("Signal card copied to clipboard");
  };

  return (
    <DashboardLayout title="AI Signals">
      {analytics && (
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
          <StatCard label="Total" value={analytics.total_signals} />
          <StatCard label="Wins" value={analytics.wins} accent="text-bullish" />
          <StatCard label="Losses" value={analytics.losses} accent="text-bearish" />
          <StatCard label="Win Rate" value={`${analytics.win_rate}%`} />
          <StatCard label="Avg R/R" value={analytics.avg_rr} />
          <StatCard label="Max DD" value={`${analytics.max_drawdown}R`} accent="text-warning" />
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-3">
        <Sel label="Market" value={f.market} onChange={(v) => setF({ ...f, market: v })} opts={["", "crypto", "forex", "stocks", "indices", "commodities"]} testid="sig-filter-market" />
        <Sel label="Direction" value={f.direction} onChange={(v) => setF({ ...f, direction: v })} opts={["", "BUY", "SELL", "WAIT"]} testid="sig-filter-direction" />
        <Sel label="Status" value={f.status} onChange={(v) => setF({ ...f, status: v })} opts={["", "ACTIVE", "TARGET 1 HIT", "TARGET 2 HIT", "STOP LOSS HIT", "EXPIRED", "CANCELLED"]} testid="sig-filter-status" />
        <div>
          <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Min Confidence: {f.min_confidence}</label>
          <input type="range" min="0" max="90" value={f.min_confidence} onChange={(e) => setF({ ...f, min_confidence: +e.target.value })} data-testid="sig-filter-confidence" className="w-40" />
        </div>
      </div>

      {!signals ? <CardSkeleton count={6} /> : signals.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="signals-empty">
          No qualifying signal currently. The AI will wait rather than force a setup.
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {signals.map((s, i) => <SignalCard key={s.asset + s.timeframe + i} s={s} onExplain={doExplain} onShare={doShare} />)}
        </div>
      )}

      <div className="mt-6"><Disclaimer /></div>

      {/* Explain dialog */}
      <Dialog open={!!explain} onOpenChange={(o) => !o && setExplain(null)}>
        <DialogContent className="max-w-lg" data-testid="explain-dialog">
          <DialogHeader><DialogTitle>Why this signal? — {explain?.asset} {explain?.timeframe}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
            {explainText || "Analyzing structured market data…"}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={!!share} onOpenChange={(o) => !o && setShare(null)}>
        <DialogContent className="max-w-md" data-testid="share-dialog">
          <DialogHeader><DialogTitle>Share Signal</DialogTitle></DialogHeader>
          {share && (
            <div>
              <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/10 to-accent/10 p-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-accent">KREONIS</div>
                <div className="mt-2 font-display text-2xl font-bold">{share.asset}</div>
                <div className="text-sm text-muted-foreground">{share.market} · {share.timeframe}</div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div>AI Bias: <span className="font-semibold">{share.direction}</span></div>
                  <div>Confidence: <span className="font-semibold">{share.confidence}/100</span></div>
                  <div>Strength: <span className="font-semibold">{share.quality}</span></div>
                  <div>R/R: <span className="font-semibold">{share.risk_reward}</span></div>
                </div>
                <div className="mt-4 text-[10px] text-muted-foreground">Probabilistic analysis · Not financial advice · Powered by KREONIS</div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={copyShare} data-testid="share-copy" className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Copy card</button>
                {["X", "Telegram", "WhatsApp", "LinkedIn"].map((p) => <span key={p} className="rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground">{p}</span>)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Sel({ label, value, onChange, opts, testid }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] uppercase text-muted-foreground">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
        {opts.map((o) => <option key={o} value={o}>{o === "" ? "All" : o}</option>)}
      </select>
    </div>
  );
}
